import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import axios from 'axios';
import logger from '../utils/logger';
import { API_URL as API } from '../utils/apiConfig';
import { optimisticSave } from '../utils/optimisticSave';

/**
 * W7 L4-B (RF4) — helper PUR exporté pour Jest : décide si on doit reset le
 * filtre subcat actuel quand l'utilisateur change la catégorie sélectionnée.
 *
 * Reset = TRUE si :
 *   - une catégorie est sélectionnée (newCategoryFilter non vide), ET
 *   - une subcat est active (currentSubcategory non vide), ET
 *   - cette subcat N'APPARTIENT PAS à la nouvelle catégorie.
 *
 * Reset = FALSE sinon (cat vide = "toutes" → toute subcat reste valide ; pas
 * de subcat active → rien à reset ; subcat dans la nouvelle cat → conserver).
 */
export function _shouldResetSubcatOnCategoryChange(currentSubcategory, newCategoryFilter, subcategoriesList) {
  if (!newCategoryFilter || !currentSubcategory) return false;
  const sub = (subcategoriesList || []).find(sc => sc.code === currentSubcategory);
  return !(sub?.categories || []).includes(newCategoryFilter);
}

export const useCurationWorkbench = () => {
  const { t } = useLanguage();
  const { showError, showSuccess } = useToast();

  // Data state
  const [stats, setStats] = useState(null);
  const [factors, setFactors] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [curationStatus, setCurationStatus] = useState('');
  const [isPublic, setIsPublic] = useState('');
  const [defaultUnit, setDefaultUnit] = useState('');
  const [reportingMethodFilter, setReportingMethodFilter] = useState('');
  const [unitsList, setUnitsList] = useState([]);
  const [sortBy, setSortBy] = useState('subcategory');
  const [sortOrder, setSortOrder] = useState('asc');

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // UI
  const [loadingFactors, setLoadingFactors] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [pendingBulkChanges, setPendingBulkChanges] = useState(null);
  const [showAISuggest, setShowAISuggest] = useState(false);
  const [jsonModalFactor, setJsonModalFactor] = useState(null);
  const [subcategoriesList, setSubcategoriesList] = useState([]);
  const [copyLoading, setCopyLoading] = useState(false);
  const [translateModal, setTranslateModal] = useState(null);
  const [linkPanelFactor, setLinkPanelFactor] = useState(null);
  // W7 L3 — flow batch alternative_subcategories : picker → preview → confirm.
  const [altSubcatPickerAction, setAltSubcatPickerAction] = useState(null);   // 'add' | 'remove' | null
  const [altSubcatPendingPreview, setAltSubcatPendingPreview] = useState(null); // { action, code, name_fr } | null
  const [altSubcatLoading, setAltSubcatLoading] = useState(false);
  // W7 L4-B — filtre catégorie (mono-valeur, "" = toutes). categoriesList chargé au mount.
  const [categoriesList, setCategoriesList] = useState([]);
  const [categoryFilter, setCategoryFilterRaw] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [searchDebounced, subcategory, curationStatus, isPublic, defaultUnit, reportingMethodFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/curation/stats`);
      setStats(res.data);
    } catch (e) { logger.error(e); }
  }, []);

  const fetchFactors = useCallback(async () => {
    setLoadingFactors(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize, sort_by: sortBy, sort_order: sortOrder });
      if (searchDebounced) params.set('search', searchDebounced);
      if (subcategory) params.set('subcategory', subcategory);
      if (curationStatus) params.set('curation_status', curationStatus);
      if (isPublic) params.set('is_public', isPublic);
      if (defaultUnit) params.set('default_unit', defaultUnit);
      if (reportingMethodFilter) params.set('reporting_method', reportingMethodFilter);

      const res = await axios.get(`${API}/api/curation/factors?${params}`);
      setFactors(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (e) { logger.error(e); }
    setLoadingFactors(false);
  }, [page, pageSize, searchDebounced, subcategory, curationStatus, isPublic, defaultUnit, reportingMethodFilter, sortBy, sortOrder]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchFactors(); }, [fetchFactors]);

  // Load subcategories + categories + units once
  useEffect(() => {
    const fetchAllSubcategories = async () => {
      try {
        const res = await axios.get(`${API}/api/subcategories`);
        // W7 L4-B : on conserve aussi le champ `categories` (liste de codes catégorie
        // par subcat) pour pouvoir filtrer les pills par catégorie côté FE (option 1
        // 4B.3 du brief audit L4).
        setSubcategoriesList(
          res.data
            .map(sc => ({
              code: sc.code,
              name_fr: sc.name_fr || sc.code,
              categories: sc.categories || [],
            }))
            .sort((a, b) => a.name_fr.localeCompare(b.name_fr))
        );
      } catch (e) { logger.error(e); }
    };
    const fetchAllCategories = async () => {
      try {
        const res = await axios.get(`${API}/api/categories`);
        // Tri par order (déjà trié serveur-side, mais sécuriser côté FE).
        setCategoriesList(
          (res.data || [])
            .map(c => ({
              code: c.code,
              name_fr: c.name_fr || c.code,
              has_factors: c.has_factors,
              subcategories: c.subcategories || [],
              order: c.order ?? 9999,
            }))
            .sort((a, b) => (a.order - b.order) || a.name_fr.localeCompare(b.name_fr))
        );
      } catch (e) { logger.error(e); }
    };
    const fetchUnits = async () => {
      try {
        const res = await axios.get(`${API}/api/curation/units`);
        setUnitsList(res.data);
      } catch (e) { logger.error(e); }
    };
    fetchAllSubcategories();
    fetchAllCategories();
    fetchUnits();
  }, []);

  const inlineEdit = useCallback(async (factorId, field, value) => {
    try {
      const currentFactor = factors.find(f => f.id === factorId);
      const result = await optimisticSave(t, () =>
        axios.patch(`${API}/api/curation/factors/${factorId}`, {
          [field]: value,
          updated_at: currentFactor?.updated_at,
        })
      );
      if (!result.ok) { fetchFactors(); return; }
      setFactors(prev => prev.map(f => f.id === factorId ? result.data : f));
      fetchStats();
    } catch (e) { logger.error(e); }
  }, [factors, fetchStats, fetchFactors, t]);

  const handleBulkAction = async (changes) => {
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/api/curation/bulk-preview`, { factor_ids: selectedIds, changes });
      setBulkPreview(res.data);
      setPendingBulkChanges(changes);
    } catch (e) { logger.error(e); }
    setBulkLoading(false);
  };

  const confirmBulkApply = async () => {
    setBulkLoading(true);
    try {
      const canonical = factors.find(f => f.id === selectedIds[0]);
      const result = await optimisticSave(t, () =>
        axios.post(`${API}/api/curation/bulk-apply`, {
          factor_ids: selectedIds, changes: pendingBulkChanges,
          updated_at: canonical?.updated_at,
        })
      );
      if (!result.ok) {
        fetchFactors();
        setBulkPreview(null);
        setPendingBulkChanges(null);
        setBulkLoading(false);
        return;
      }
      setSelectedIds([]);
      setBulkPreview(null);
      setPendingBulkChanges(null);
      fetchFactors();
      fetchStats();
    } catch (e) { logger.error(e); }
    setBulkLoading(false);
  };

  const handleAISuggestApply = async (suggestions) => {
    setShowAISuggest(false);
    for (const s of suggestions) {
      await inlineEdit(s.factor_id, 'name_simple_fr', s.name_simple_fr);
      await inlineEdit(s.factor_id, 'name_simple_de', s.name_simple_de);
    }
    fetchFactors();
    fetchStats();
  };

  const handleCopyOriginals = async (lang, sourceField = 'original') => {
    setCopyLoading(true);
    try {
      const res = await axios.post(`${API}/api/curation/bulk-copy-originals`, { factor_ids: selectedIds, lang, source_field: sourceField });
      showSuccess(t('curation.alerts.namesCopied').replace('{modified}', res.data.modified_count).replace('{skipped}', res.data.skipped_count));
      fetchFactors();
      fetchStats();
    } catch (e) { logger.error(e); }
    setCopyLoading(false);
  };

  const handleLinkLocation = (factorId, locationFactorId) => inlineEdit(factorId, 'location_factor_id', locationFactorId);
  const handleUnlinkLocation = (factorId) => inlineEdit(factorId, 'location_factor_id', null);

  // W7 L4-B (RF4) — setter de categoryFilter qui reset la subcat sélectionnée si
  // elle n'appartient PAS à la nouvelle catégorie (évite l'état incohérent : pill
  // non visible mais filtre actif).
  const setCategoryFilter = useCallback((newCat) => {
    if (_shouldResetSubcatOnCategoryChange(subcategory, newCat, subcategoriesList)) {
      setSubcategory('');
    }
    setCategoryFilterRaw(newCat);
  }, [subcategory, subcategoriesList]);

  // W7 L3 — flow batch alternative_subcategories
  const openAltSubcatPicker = useCallback((action) => {
    // action = 'add' | 'remove' — visible UNIQUEMENT si sélection non vide (garde côté UI).
    setAltSubcatPickerAction(action);
  }, []);

  const cancelAltSubcat = useCallback(() => {
    setAltSubcatPickerAction(null);
    setAltSubcatPendingPreview(null);
  }, []);

  const handleAltSubcatPicked = useCallback((subcat) => {
    // subcat = { code, name_fr } — émis par AltSubcatPicker.
    setAltSubcatPendingPreview({
      action: altSubcatPickerAction,
      code: subcat.code,
      name_fr: subcat.name_fr,
    });
    setAltSubcatPickerAction(null);
  }, [altSubcatPickerAction]);

  const confirmBulkAltSubcat = useCallback(async () => {
    if (!altSubcatPendingPreview) return;
    const { action, code } = altSubcatPendingPreview;
    setAltSubcatLoading(true);
    try {
      const canonical = factors.find(f => f.id === selectedIds[0]);
      const res = await axios.post(`${API}/api/curation/bulk-alternative-subcategory`, {
        factor_ids: selectedIds,
        subcategory_code: code,
        action,
        updated_at: canonical?.updated_at,
      });
      const r = res.data || {};
      const summaryKey = action === 'add'
        ? 'curation.alerts.bulkAltSubcatSummaryAdd'
        : 'curation.alerts.bulkAltSubcatSummaryRemove';
      showSuccess(
        t(summaryKey)
          .replace('{code}', code)
          .replace('{modified}', r.modified ?? 0)
          .replace('{skipped}', r.skipped_is_principal ?? 0)
          .replace('{notFound}', r.not_found ?? 0)
      );
      fetchFactors();
      fetchStats();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) {
        showError(t('curation.alerts.subcatCodeUnknown').replace('{code}', code));
      } else if (status === 409) {
        showError(t('curation.alerts.concurrencyConflict'));
        fetchFactors();
      } else {
        logger.error('bulk-alternative-subcategory failed:', e);
        showError(t('curation.alerts.bulkAltSubcatError'));
      }
    } finally {
      setAltSubcatPendingPreview(null);
      setAltSubcatLoading(false);
    }
  }, [altSubcatPendingPreview, factors, selectedIds, fetchFactors, fetchStats]);

  const handleTranslateApply = (modifiedCount) => {
    setTranslateModal(null);
    showSuccess(t('curation.alerts.translationsApplied').replace('{count}', modifiedCount));
    fetchFactors();
    fetchStats();
  };

  const handleCellNavigate = useCallback((cellId, direction) => {
    const match = cellId.match(/row-(\d+)-col-(\d+)/);
    if (!match) return;
    let rowIdx = parseInt(match[1]);
    let colIdx = parseInt(match[2]);

    if (direction === 'next' || direction === 'down') {
      if (direction === 'next') {
        colIdx++;
        if (colIdx > 1) { colIdx = 0; rowIdx++; }
      } else {
        rowIdx++;
      }
    } else if (direction === 'prev') {
      colIdx--;
      if (colIdx < 0) { colIdx = 1; rowIdx--; }
    } else if (direction === 'mark-reviewed') {
      if (factors[rowIdx]) {
        inlineEdit(factors[rowIdx].id, 'curation_status', 'reviewed');
      }
      rowIdx++;
      colIdx = 0;
    }

    if (rowIdx >= 0 && rowIdx < factors.length) {
      const targetId = `row-${rowIdx}-col-${colIdx}`;
      setTimeout(() => {
        const el = document.querySelector(`[data-cell-id="${targetId}"]`);
        if (el) el.click();
      }, 50);
    }
  }, [factors, inlineEdit]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    const pageIds = factors.map(f => f.id);
    const allSelected = pageIds.every(id => selectedIds.includes(id));
    setSelectedIds(prev => allSelected ? prev.filter(id => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]);
  };
  const allPageSelected = factors.length > 0 && factors.every(f => selectedIds.includes(f.id));

  return {
    stats, factors, total, totalPages,
    page, setPage, search, setSearch, subcategory, setSubcategory,
    curationStatus, setCurationStatus, isPublic, setIsPublic,
    defaultUnit, setDefaultUnit, reportingMethodFilter, setReportingMethodFilter,
    unitsList, sortBy, sortOrder, toggleSort,
    selectedIds, setSelectedIds, toggleSelect, toggleSelectAll, allPageSelected,
    loadingFactors, bulkLoading, copyLoading, bulkPreview, setBulkPreview,
    pendingBulkChanges, setPendingBulkChanges, showAISuggest, setShowAISuggest,
    jsonModalFactor, setJsonModalFactor, subcategoriesList,
    translateModal, setTranslateModal, linkPanelFactor, setLinkPanelFactor,
    inlineEdit, handleBulkAction, confirmBulkApply,
    handleAISuggestApply, handleCopyOriginals,
    handleLinkLocation, handleUnlinkLocation, handleTranslateApply,
    handleCellNavigate, t,
    // W7 L3 — batch alternative_subcategories
    altSubcatPickerAction, altSubcatPendingPreview, altSubcatLoading,
    openAltSubcatPicker, handleAltSubcatPicked, cancelAltSubcat, confirmBulkAltSubcat,
    // W7 L4-B — filtre catégorie (dropdown + filtrage pills + RF4 reset)
    categoriesList, categoryFilter, setCategoryFilter,
  };
};
